import {
  AlertTriangle,
  Ban,
  Construction,
  History,
  Inbox,
  RefreshCw,
  WifiOff,
} from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "@/lib/cn";
import { formatTimestamp } from "@/lib/format";

import { Button } from "./Button";
import { Notice } from "./Notice";
import { Skeleton } from "./Skeleton";

/**
 * The five states every data surface in this application must be able to
 * render. Having them as named components rather than ad-hoc markup is what
 * stops a screen from silently rendering blank — the failure mode the product
 * reference has on its project Overview.
 *
 * The two notices at the bottom of this file are not a sixth and seventh member
 * of that set. A surface state renders *instead of* content; a notice renders
 * *above* content that is present and usable. They are the two page-level
 * notices this product has agreed wording for, composed from the general
 * `Notice` primitive in `./Notice` — which #527 moved out of this file once six
 * other surfaces needed it too.
 */

function Frame({
  icon,
  title,
  description,
  action,
  tone = "neutral",
  className,
}: {
  icon: ReactNode;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  tone?: "neutral" | "danger";
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-3 rounded-md border border-dashed border-border bg-card px-6 py-12 text-center",
        className,
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          "flex h-10 w-10 items-center justify-center rounded-md",
          tone === "danger"
            ? "bg-destructive/10 text-destructive"
            : "bg-muted text-muted-foreground",
        )}
      >
        {icon}
      </span>
      <div className="space-y-1">
        <p className="text-sm font-semibold text-foreground">{title}</p>
        {description ? (
          <div className="mx-auto max-w-md text-sm text-muted-foreground">
            {description}
          </div>
        ) : null}
      </div>
      {action}
    </div>
  );
}

export function LoadingState({
  label,
  rows = 3,
  className,
}: {
  label: string;
  rows?: number;
  className?: string;
}) {
  return (
    <div
      role="status"
      aria-busy="true"
      aria-live="polite"
      className={cn("space-y-3", className)}
    >
      <span className="sr-only">{label}</span>
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton key={index} className="h-14 w-full" />
      ))}
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
  className,
}: {
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <Frame
      icon={<Inbox className="h-5 w-5" />}
      title={title}
      {...(description !== undefined ? { description } : {})}
      {...(action !== undefined ? { action } : {})}
      {...(className !== undefined ? { className } : {})}
    />
  );
}

export function ErrorState({
  title = "Something went wrong",
  description,
  onRetry,
  className,
}: {
  title?: string;
  description?: ReactNode;
  onRetry?: () => void;
  className?: string;
}) {
  return (
    <div role="alert">
      <Frame
        tone="danger"
        icon={<AlertTriangle className="h-5 w-5" />}
        title={title}
        {...(description !== undefined ? { description } : {})}
        {...(className !== undefined ? { className } : {})}
        {...(onRetry
          ? {
              action: (
                <Button variant="outline" onClick={onRetry}>
                  <RefreshCw aria-hidden="true" className="h-4 w-4" />
                  Try again
                </Button>
              ),
            }
          : {})}
      />
    </div>
  );
}

export function DeniedState({
  title = "You do not have access to this",
  description = "Ask a workspace administrator to grant access to this project.",
  action,
  className,
}: {
  title?: string;
  description?: ReactNode;
  /** The one control that can actually change the answer, when one exists. */
  action?: ReactNode;
  className?: string;
}) {
  return (
    <Frame
      icon={<Ban className="h-5 w-5" />}
      title={title}
      description={description}
      {...(action !== undefined ? { action } : {})}
      {...(className !== undefined ? { className } : {})}
    />
  );
}

/**
 * Shown when a request is waiting on a connection rather than on a server.
 *
 * This is not a sixth kind of failure so much as the one that was previously
 * invisible: a paused query keeps `status: "pending"`, so before issue #215 it
 * rendered as a skeleton that never resolved. It is deliberately distinct from
 * the neighbouring states — the resource is not denied, absent, or broken, and
 * the service never answered at all, so the honest thing to say is that the
 * connection is the problem and the view returns on its own.
 *
 * The retry exists because the browser's own offline signal can lag a
 * connection that has already come back. Wiring it is not free: a paused
 * query answers a bare `refetch()` by returning the paused retryer's promise
 * without firing anything, so `onRetry` must come from `useRetryPausedQuery`
 * — or from `QueryPendingState`, which builds it that way — not from a
 * `refetch` reference.
 */
export function OfflineState({
  title = "You appear to be offline",
  description = "This view could not reach the Editor Service. It loads on its own when the connection returns.",
  onRetry,
  className,
}: {
  title?: string;
  description?: ReactNode;
  onRetry?: () => void;
  className?: string;
}) {
  return (
    <div role="status" aria-live="polite">
      <Frame
        icon={<WifiOff className="h-5 w-5" />}
        title={title}
        description={description}
        {...(className !== undefined ? { className } : {})}
        {...(onRetry
          ? {
              action: (
                <Button variant="outline" onClick={onRetry}>
                  <RefreshCw aria-hidden="true" className="h-4 w-4" />
                  Try again
                </Button>
              ),
            }
          : {})}
      />
    </div>
  );
}

/**
 * Shown above data that is present but known to be out of date: a background
 * refetch failed while the previous response was still in the cache.
 *
 * Discarding good data on a failed refresh is worse than showing it — the
 * clinician loses the page — but showing it silently is worse still, because
 * nothing on screen would admit that it is older than it looks.
 */
export function StaleDataNotice({
  updatedAt,
  onRetry,
  visible = true,
  className,
}: {
  /** Epoch milliseconds of the last successful response. */
  updatedAt: number;
  onRetry: () => void;
  /**
   * Keep the component mounted and toggle this value so assistive technology
   * observes the message being added to the existing live region.
   */
  visible?: boolean;
  className?: string;
}) {
  return (
    <Notice
      icon={<History className="h-4 w-4" />}
      visible={visible}
      action={
        <Button variant="outline" onClick={onRetry}>
          <RefreshCw aria-hidden="true" className="h-4 w-4" />
          Refresh
        </Button>
      }
      {...(className !== undefined ? { className } : {})}
    >
      The last refresh failed. Showing data from {formatTimestamp(updatedAt)}.
    </Notice>
  );
}

/**
 * Shown above a page whose sections did not all load.
 *
 * Offers no recovery **by default**, on purpose: each failed section renders its
 * own, and which recovery is correct depends on why that section failed — a 501
 * gets none at all. A page-level "try again" cannot say what it would retry.
 *
 * `action` is opt-in for the one shape that paragraph does not cover: a surface
 * that reads several sections ITSELF and renders none of them separately, so
 * there is no per-section recovery for this notice to defer to (#372's adoption
 * surface reads all seven to attribute locks and provenance to pins). The caller
 * supplies a control that names what it would retry.
 */
export function PartialDataNotice({
  description = "Some sections of this page could not be loaded. Each one explains its own state below; the rest of the page is unaffected.",
  action,
  visible = true,
  className,
}: {
  description?: ReactNode;
  /** A recovery, for a surface that renders no per-section one. */
  action?: ReactNode;
  /**
   * Keep the component mounted and toggle this value so assistive technology
   * observes the message being added to the existing live region.
   */
  visible?: boolean;
  className?: string;
}) {
  return (
    <Notice
      icon={<AlertTriangle className="h-4 w-4" />}
      visible={visible}
      {...(action !== undefined ? { action } : {})}
      {...(className !== undefined ? { className } : {})}
    >
      {description}
    </Notice>
  );
}

export function UnavailableState({
  title,
  description,
  blockedBy,
  className,
}: {
  title: string;
  description: ReactNode;
  /** The contract or ticket this surface is waiting on. */
  blockedBy?: string;
  className?: string;
}) {
  return (
    <Frame
      icon={<Construction className="h-5 w-5" />}
      title={title}
      description={
        <>
          <div>{description}</div>
          {blockedBy ? (
            <p className="mt-2 font-mono text-xs">{blockedBy}</p>
          ) : null}
        </>
      }
      {...(className !== undefined ? { className } : {})}
    />
  );
}
